import React, {
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import {
    Text,
    View,
    Keyboard,
    TouchableOpacity, useWindowDimensions, Modal, TouchableWithoutFeedback,
} from 'react-native-web';
import PropTypes from 'prop-types';

// Custom components
import { Button } from '../component_button';

// Theme
import styles from './styles';

// Globals
import GLOBAL from '../../configs/config_global';

export function CustomBottomSheetModal(props, ref) {
    const { height: screenHeight } = useWindowDimensions();
    const {
        snapPoints,
        title,
        hideCloseButton,
        renderExtraFooterComp,
        enableDragToClose,
        tapBackdropToClose,
        onDismiss,
        onPresent,
        showCancelButton,
        onCancelPressed,
        containerStyle,
        childContainerStyle,
        renderTopRightButton,
        stacking,
        topLeftCancelText,
        extraProps,
        enableHandleDragging,
        closeButtonText,
        closeButtonOnPressed,
        closeButtonOnPressedOnlyAddExtra,
        headerContainerStyle,
        closeButtonDisabled,
        closeButtonStyle,
        closeButtonColor,
        closeButtonTextColor,
        showGradient,
        renderTopLeftButton,
        hideHandle,

        // Test IDs,
        testID,
        topLeftButtonTestID,
        bottomButtonTestID,
        childContainerTestID,
        mainContainerTestID,
        titleTestID
    } = props;

    // region Refs
    const bottomSheetRef = useRef(null);
    // endregion

    // region Local State
    const [sheetVisible, setSheetVisible] = useState(false);
    const [cancelWidth, setCancelWidth] = useState(0);
    const [sheetIndex, setSheetIndex] = useState(-1);
    // endregion

    // region Expose Methods
    useImperativeHandle(
        ref,
        () => ({
            close: () => setSheetVisible(false),
            open: () => {
                setSheetVisible(true);
                onPresent();
            }
        }),
        [bottomSheetRef]
    );
    // endregion

    // region Back Handler
    const _onCancel = () => {
        if (!!onCancelPressed) {
            onCancelPressed();
        }
        else {
            setSheetVisible(false);
        }
    };
    // endregion

    // region Render Header
    const renderHeader = () => (
        (title.length > 0) &&
        <View style={[styles.header].concat(headerContainerStyle)}>
            {/* Cancel Button */}
            {
                showCancelButton ?
                    <TouchableOpacity
                        testID={topLeftButtonTestID}
                        onPress={_onCancel}
                        style={{ zIndex: 99 }}
                        onLayout={(e) => !cancelWidth && setCancelWidth(e.nativeEvent.layout.width + 36)}>
                        <Text style={styles.cancelText}>
                            {topLeftCancelText || 'Cancel'}
                        </Text>
                    </TouchableOpacity>
                    :
                    ((!!renderTopLeftButton && (typeof renderTopLeftButton === 'function')) && renderTopLeftButton())
            }

            {/* Title */}
            <View pointerEvents={'none'} style={[styles.titleContainer, { paddingHorizontal: cancelWidth }]}>
                <Text testID={titleTestID} style={styles.title} numberOfLines={2}>
                    {title}
                </Text>
            </View>

            {/* Blank Space */}
            <View style={{ flex: 1 }} />

            {/* Top Right Button */}
            {typeof renderTopRightButton === 'function' && renderTopRightButton()}
        </View>
    )
    // endregion

    // Footer
    const renderFooter = () => (
        <View style={{ position: 'absolute', left: 0, bottom: 0, right: 0, backgroundColor: 'white', paddingTop: 8, paddingBottom: 24, paddingHorizontal: 24 }}>
            {/* Extra Component */}
            {(typeof renderExtraFooterComp === 'function') && renderExtraFooterComp()}

            {/* Close Button */}
            {
                !hideCloseButton &&
                <Button
                    testID={bottomButtonTestID}
                    text={closeButtonText || 'Close'}
                    textColor={closeButtonTextColor || 'white'}
                    onPress={() => {
                        if (!!closeButtonOnPressed && (typeof closeButtonOnPressed === 'function')) {
                            if (closeButtonOnPressedOnlyAddExtra) {
                                setSheetVisible(false);
                                setTimeout(closeButtonOnPressed, 250);
                            }
                            else {
                                closeButtonOnPressed();
                            }
                        }
                        else {
                            setSheetVisible(false);
                        }
                    }}
                    style={[styles.closeButton, { backgroundColor: closeButtonColor || GLOBAL.COLOR.MAIN }, closeButtonStyle]}
                    useRoundButton={true}
                    locked={closeButtonDisabled}
                />
            }
        </View>
    );

    // Main render
    return(
        <Modal
            visible={sheetVisible}
            animationType={'fade'}
            transparent={true}>
            <View style={{ flex: 1, width: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', alignItems: 'center', justifyContent: 'flex-end' }}>
                <View style={{ width: '30%', height: '70%', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderColor: GLOBAL.COLOR.DIVIDER, overflow: 'hidden', backgroundColor: 'white' }}>
                    {/* Header */}
                    {renderHeader()}

                    {/* Content */}
                    {props.children}

                    {/* Footer */}
                    {renderFooter()}
                </View>
            </View>
        </Modal>
    );
}

// Forward Refs
CustomBottomSheetModal = React.forwardRef(CustomBottomSheetModal);
CustomBottomSheetModal.name = 'CustomBottomSheetModal';

// region Props
CustomBottomSheetModal.propTypes = {
    snapPoints: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    title: PropTypes.string,
    hideCloseButton: PropTypes.bool,
    showCancelButton: PropTypes.bool,
    onCancelPressed: PropTypes.func,
    renderExtraFooterComp: PropTypes.any,
    enableDragToClose: PropTypes.bool,
    tapBackdropToClose: PropTypes.bool,
    onDismiss: PropTypes.func,
    onPresent: PropTypes.func,
    containerStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    childContainerStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    headerContainerStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    renderTopRightButton: PropTypes.func,
    stacking: PropTypes.bool,
    topLeftCancelText: PropTypes.string,
    extraProps: PropTypes.object,
    enableHandleDragging: PropTypes.bool,
    closeButtonText: PropTypes.string,
    closeButtonOnPressed: PropTypes.func,
    closeButtonOnPressedOnlyAddExtra: PropTypes.bool,
    closeButtonDisabled: PropTypes.bool,
    showGradient: PropTypes.bool,
    testID: PropTypes.string,
    topLeftButtonTestID: PropTypes.string,
    bottomButtonTestID: PropTypes.string,
    childContainerTestID: PropTypes.string,
    mainContainerTestID: PropTypes.string,
    titleTestID: PropTypes.string,
    closeButtonStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    closeButtonColor: PropTypes.string,
    closeButtonTextColor: PropTypes.string,
    renderTopLeftButton: PropTypes.func,
    hideHandle: PropTypes.bool,
    fullWidthForTablet: PropTypes.bool,
};

CustomBottomSheetModal.defaultProps = {
    snapPoints: [],
    title: '',
    hideCloseButton: false,
    showCancelButton: false,
    onCancelPressed: null,
    renderExtraFooterComp: null,
    enableDragToClose: true,
    tapBackdropToClose: true,
    onDismiss: () => {},
    onPresent: () => Keyboard.dismiss(),
    containerStyle: {},
    childContainerStyle: {},
    headerContainerStyle: {},
    renderTopRightButton: () => <View />,
    stacking: false,
    topLeftCancelText: null,
    extraProps: {},
    enableHandleDragging: true,
    closeButtonText: '',
    closeButtonOnPressed: null,
    closeButtonOnPressedOnlyAddExtra: false,
    closeButtonDisabled: false,
    showGradient: false,
    testID: null,
    topLeftButtonTestID: null,
    bottomButtonTestID: null,
    childContainerTestID: null,
    mainContainerTestID: null,
    titleTestID: null,
    closeButtonStyle: null,
    closeButtonColor: null,
    closeButtonTextColor: null,
    renderTopLeftButton: null,
    hideHandle: false,
    fullWidthForTablet: false,
};
// endregion
